import * as mobx from 'mobx';
import * as IoC from 'typescript-ioc';
import { FormattedMessage, IntlShape } from 'react-intl';
import { Category, IAssetsDAO, IAssetsListItemDTO, IAssetsOrganizationListItemDTO, IAssetsSpeakersDetailsDTO, ITitleAssetUrlDTO, UploadType } from '../../../../api/assets.dao';
import { TGetReportsSortKeys } from '../../../../api/reports.dao';
import { SortOrder } from '../../../../api/shared.dto';
import { IAlertsService } from '../../../../infrastructure/alerts';
import { IErrorParsing } from '../../../../infrastructure/error-parsing';
import TrackingServiceProvider from '../../../../tracking-service/tracking-service-provider';
import { alertServerError } from '../../../global/modal/alerts-host/alert-templates';
import { FILE_VALIDATION_SIZE_ERROR_MESSAGE, IMAGE_VALIDATION_MIN_WIDTH_HEIGHT_ERROR_MESSAGE } from '../../../../constants/validation-errors';
import { checkFileMinWidthHeight, getFileType, isFileFormatValid, isFileSizeValid, replaceCommaAndSemicolon } from '../../../../infrastructure/utils/common.utils';
import {
    ASSETS_VALIDATION_FORMAT_ERROR_MESSAGE,
    DIMENSIONS_PROFILE_IMAGE,
    IMAGE_VALIDATION_FORMAT_ERROR_MESSAGE,
    IMAGE_VALIDATION_SIZE_ERROR_MESSAGE,
    SUPPORTED_ASSETS_TYPES,
    SUPPORTED_IMAGE_TYPES
} from '../../../../constants/file.constants';
import { ASSETS_PAGE_TRACKING_EVENTS, TRACKING_EVENTS } from '../../../../tracking-service/tracking-values';
import { IGlobalStorage } from '../../../../infrastructure/global-storage';
import { PagingViewModel } from '../../../global/_paging/paging-viewmodels/regular-paging.viewmodel';
import React from 'react';
import { Space } from '../../../../constants/html-character-entities';
import { hasPermission } from '../../../../infrastructure/utils/has-permissions';
import { Permission } from '../../../../api/roles.dao';
import { isFilePublic } from '../../../../infrastructure/utils/file.utils';

export interface IAssetsGroup {
    id: string;
    label: string;
    uploadType: UploadType;
}

export const MAX_ASSET_FILE_SIZE = 1073741824;

export const AssetsDefaultConfiguration = [
    {
        type: Category.UNCATEGORIZED,
        label: 'Uncategorized Files'
    },
];

class AssetsViewModel {
    @mobx.observable
    public loading: boolean;
    @mobx.observable
    public loadingOrganizations: boolean;
    @mobx.observable
    public isUploadLoading: boolean;
    @mobx.observable
    public sortOrder: SortOrder;
    @mobx.observable
    public sortField: TGetReportsSortKeys;
    @mobx.observable
    public collapsedGroups: string[];
    @mobx.observable
    public assetsList: IAssetsListItemDTO;
    @mobx.observable
    public assetsSpeakersGroupedList: IAssetsOrganizationListItemDTO[];
    @mobx.observable
    public selectedFiles: File[];
    @mobx.observable
    public fileError: string | JSX.Element;
    private paginationVM: PagingViewModel;
    @mobx.observable
    public isLinkCreating: Set<number> = new Set();

    public constructor(
        public readonly accountId: number,
        public readonly eventId: number,
        private readonly intl: IntlShape,
        @IoC.Inject private readonly assetsDAO: IAssetsDAO = IoC.Container.get(IAssetsDAO),
        @IoC.Inject private readonly alertsService: IAlertsService = IoC.Container.get(IAlertsService),
        @IoC.Inject private readonly errorParsingService: IErrorParsing = IoC.Container.get(IErrorParsing),
        @IoC.Inject private readonly trackingService: TrackingServiceProvider = IoC.Container.get(TrackingServiceProvider),
        @IoC.Inject private readonly globalStorage: IGlobalStorage = IoC.Container.get(IGlobalStorage),
    ) {
        this.paginationVM = new PagingViewModel();
        mobx.reaction(this.paginationVM.getCurrentPage, this.fetchAssetsOrganizations);
        this.loading = false;
        this.isUploadLoading = false;
        this.collapsedGroups = [];
        this.assetsList = { files: [] };
        this.selectedFiles = [];
        this.fileError = null;
    }

    public static handleApiException(e: Error, title: string = 'Error'): void {
        if (e) alertServerError(title, IoC.Container.get(IErrorParsing).extractMessage(e));
    }

    public initialize = (): void => {
        this.fetchAssets();
        this.fetchAssetsOrganizations();
    };

    @mobx.action
    public resetValues = (): void => {
        this.fileError = null;
        this.collapsedGroups = [];
    };

    @mobx.action
    public validateSelectedFiles = async (file: File): Promise<File> => {
        const isCorrectFileType = await isFileFormatValid(file, false, SUPPORTED_ASSETS_TYPES);
        let errorMessage = null;
        if (!isCorrectFileType) {
            errorMessage = ASSETS_VALIDATION_FORMAT_ERROR_MESSAGE;
        } else if (!isFileSizeValid(file, MAX_ASSET_FILE_SIZE)) {
            errorMessage = FILE_VALIDATION_SIZE_ERROR_MESSAGE(1, 'GB');
        }
        mobx.runInAction(() => {
            this.fileError = errorMessage;
        });
        if (!errorMessage) {
            return replaceCommaAndSemicolon(file);
        }
        return null;
    };

    @mobx.action
    public uploadAssets = async (files: File[]): Promise<void> => {
        try {
            this.fileError = null;
            const validationResults = await Promise.all(
                files.map(file => this.validateSelectedFiles(file))
            );
            const uploadedAssets = validationResults.filter(Boolean);
            if (uploadedAssets.length) {
                this.updateAssets(uploadedAssets);
            }
            mobx.runInAction(() => {
                if (uploadedAssets.length) {
                    this.selectedFiles = uploadedAssets;
                }
            });
            // eslint-disable-next-line no-empty
        } catch (e) {}
    };

    @mobx.action
    public onDeleteAsset = (objectKey: string): void => {
        mobx.runInAction(() => {
            this.assetsList.files = [...this.assetsList.files.filter(file => file.objectKey !== objectKey)];
        });
    };

    @mobx.action
    public onDeleteSpeaker = (organizationId: number, attendeeId: number): void => {
        mobx.runInAction(() => {
            const { indexOrg, indexSpeakerInOrg } = this.getIndexesOrgAndSpeaker(organizationId, attendeeId);
            if (indexOrg > -1 && indexSpeakerInOrg > -1) {
                this.assetsSpeakersGroupedList[indexOrg].speakers[indexSpeakerInOrg].profileImage = null;
                this.assetsSpeakersGroupedList[indexOrg].speakers[indexSpeakerInOrg].profileImageWithUrl = null;
            }
        });
    };

    @mobx.action
    public fetchAssets = async (): Promise<void> => {
        try {
            mobx.runInAction(() => {
                this.loading = true;
            });

            const response = await this.assetsDAO.getAssets({
                eventId: this.eventId,
                accountId: this.accountId
            });

            mobx.runInAction(() => {
                this.assetsList = response;
            });

        } catch (e) {
            AssetsViewModel.handleApiException(e);
        } finally {
            mobx.runInAction(() => {
                this.loading = false;
            });
        }
    };

    @mobx.action
    public fetchAssetsOrganizations = async (): Promise<void> => {
        try {
            mobx.runInAction(() => {
                this.loadingOrganizations = true;
            });

            const response = await this.assetsDAO.getAssetsSpeakers({
                eventId: this.eventId,
                accountId: this.accountId,
                size: this.paginationVM.getPageSize(),
                page: this.paginationVM.getCurrentPageForBE()
            });

            mobx.runInAction(() => {
                this.assetsSpeakersGroupedList = this.groupSpeakersByOrganization(response.content);
                this.paginationVM.setTotalRecords(response.totalElements);
            });

        } catch (e) {
            AssetsViewModel.handleApiException(e);
        } finally {
            mobx.runInAction(() => {
                this.loadingOrganizations = false;
            });
        }
    };

    private groupSpeakersByOrganization(speakers: IAssetsSpeakersDetailsDTO[]): IAssetsOrganizationListItemDTO[] {
        const organizationIds = [...new Set<number>(speakers.map(speaker => speaker.organizationId))];
        return organizationIds.map(organizationId => {
            const orgSpeakers: IAssetsSpeakersDetailsDTO[] = speakers
                .filter(speaker => speaker.organizationId === organizationId)
                .map(speaker => ({
                    ...speaker,
                    validationProfileImageError: null,
                    isLoadingProfileImage: false,
                    isAdded: false,
                    isBiographyAdded: false
                }));
            return {
                name: orgSpeakers[0]?.organizationName || '',
                organizationId: organizationId,
                speakers: orgSpeakers
            };
        });
    }

    @mobx.action
    public updateAssets = async (files: File[]): Promise<void> => {
        try {
            mobx.runInAction(() => {
                this.isUploadLoading = true;
            });

            const response = await this.assetsDAO.updateAssets({
                    eventId: this.eventId,
                    accountId: this.accountId,
                }, files
            );

            mobx.runInAction(() => {
                this.assetsList = {...response} ;
                this.trackAssetsMixPanel(TRACKING_EVENTS.UPLOAD_EVENT_ASSET);
            });

        } catch (e) {
            AssetsViewModel.handleApiException(e);
        } finally {
            mobx.runInAction(() => {
                this.selectedFiles = [];
                this.isUploadLoading = false;
            });
        }
    };

    public findOrgIndexInAssetsSpeakersGroupedList = (organizationId: number): number => {
        return this.assetsSpeakersGroupedList?.findIndex(assetSpeakerGroup => assetSpeakerGroup.organizationId === organizationId);
    };

    public findSpeakerIndexInAssetsSpeakersGroupedList = (indexOrg: number, attendeeId: number): number => {
        return this.assetsSpeakersGroupedList?.[indexOrg]?.speakers.findIndex(speaker => speaker.attendeeId === attendeeId);
    };

    @mobx.action
    public removeAssetFromUncategorizedFiles = (objectKey: string): void => {
        this.assetsList.files = this.assetsList.files.filter(file => file.objectKey !== objectKey);
    };

    @mobx.action
    public validateFileTypeProfileImage = async (file: File, title: string): Promise<boolean> => {
        if (file) {
            return await isFileFormatValid(file, false);
        } else {
            return [...SUPPORTED_IMAGE_TYPES, 'image/svg'].includes('image/' + getFileType(title));
        }
    };

    public getIndexesOrgAndSpeaker = (organizationId: number, attendeeId: number): { indexOrg: number; indexSpeakerInOrg: number } => {
        const indexOrg = this.findOrgIndexInAssetsSpeakersGroupedList(Number(organizationId));
        let indexSpeakerInOrg = null;
        if (indexOrg > -1) {
            indexSpeakerInOrg = this.findSpeakerIndexInAssetsSpeakersGroupedList(indexOrg, Number(attendeeId));
        }
        return { indexOrg: indexOrg, indexSpeakerInOrg: indexSpeakerInOrg };
    };

    @mobx.action
    public setProfileImageValidationError = (
        errorMessage: string,
        indexOrg: number,
        indexSpeakerInOrg: number
    ): void => {
        if (indexOrg > -1 && indexSpeakerInOrg > -1) {
            this.assetsSpeakersGroupedList[indexOrg].speakers[indexSpeakerInOrg]
                .validationProfileImageError = errorMessage;
        }
    };

    @mobx.action
    public resetProfileImageValidationError = (
        indexOrg: number,
        indexSpeakerInOrg: number
    ): void => {
        if (indexOrg > -1 && indexSpeakerInOrg > -1) {
            this.assetsSpeakersGroupedList[indexOrg].speakers[indexSpeakerInOrg]
                .validationProfileImageError = null;
        }
    };

    @mobx.action
    public setIsLoadingProfileImage = (isLoading: boolean, indexOrg: number, indexSpeakerInOrg: number): void => {
        if (indexOrg > -1 && indexSpeakerInOrg > -1) {
            this.assetsSpeakersGroupedList[indexOrg].speakers[indexSpeakerInOrg].isLoadingProfileImage = isLoading;
        }
    };

    @mobx.action
    public setProfileImage = (draggingFile: ITitleAssetUrlDTO, indexOrg: number, indexSpeakerInOrg: number): void => {
        if (indexOrg > -1 && indexSpeakerInOrg > -1) {
            this.assetsSpeakersGroupedList[indexOrg].speakers[indexSpeakerInOrg].profileImage = {
                title: draggingFile.title,
                objectKey: draggingFile.objectKey,
            };
            this.assetsSpeakersGroupedList[indexOrg].speakers[indexSpeakerInOrg].profileImageWithUrl = {
                title: draggingFile.title,
                assetUrl: draggingFile.assetUrl,
            };
            this.assetsSpeakersGroupedList[indexOrg].speakers[indexSpeakerInOrg].isAdded = true;
            setTimeout(() => {
                mobx.runInAction(() => this.assetsSpeakersGroupedList[indexOrg].speakers[indexSpeakerInOrg].isAdded = false);
            }, 3000);
        }
    };

    @mobx.action
    public checkIsFileReplacing = async (
        organizationId: string,
        attendeeId: string,
        uploadProfileImageFromUncategorizedApiCall: () => void
    ): Promise<void> => {
        const { indexOrg, indexSpeakerInOrg } = this.getIndexesOrgAndSpeaker(Number(organizationId), Number(attendeeId));
        if (indexOrg > -1 && indexSpeakerInOrg > -1) {
            if (this.assetsSpeakersGroupedList[indexOrg].speakers[indexSpeakerInOrg].profileImage.objectKey) {
                if (this.fileDownloadAllowed) {
                    this.alertsService.confirm({
                        message: (
                            <span className='alert-message'>
                                <FormattedMessage
                                    id='oe.confirm.doYouWantTo'
                                    description='Confirm message'
                                    defaultMessage='Do you want to'
                                />
                                <Space />
                                <span className='alert-message--confirm-safe'>
                                    <FormattedMessage
                                        id='oe.user.replaceThisImage'
                                        description='Confirm message'
                                        defaultMessage='replace this image'
                                    />
                                </span>
                                ? <Space />
                                <FormattedMessage
                                    id='oe.confirm.imageCannotBeRecovered'
                                    description='Confirm message'
                                    defaultMessage='The original image will be deleted and cannot be recovered'
                                />.
                            </span>
                        ),
                        confirmButtonProps: {
                            text: 'Replace',
                            modifier: 'confirm-safe',
                            handler: () => uploadProfileImageFromUncategorizedApiCall()
                        },
                        cancelButtonProps: {
                            modifier: 'cancel',
                        },
                    });
                } else {
                    this.showDragAndDropIsNotAllowedNotification();
                }
            }
        }
    };

    private showDragAndDropIsNotAllowedNotification(): void {
        this.alertsService.alert({
            message: (
                <span className='alert-message'>
                    <FormattedMessage
                        id='oe.notification.replaceImageNotAllowed'
                        description='Info message'
                        defaultMessage={'You do not have permission to drag and drop this file.' +
                            ' Please click the Add Profile image button to add a new image or click the file name to replace an existing image.'}
                    />
                </span>
            ),
            okButtonProps: {
                text: 'OK'
            }
        });
    }

    private get fileDownloadAllowed(): boolean {
        return hasPermission(Permission.DOWNLOAD_EVENT_ASSETS);
    }

    @mobx.action
    private completeProfileImageUploading = (
        updatedProfileImage: ITitleAssetUrlDTO,
        draggingFile: ITitleAssetUrlDTO,
        indexOrg: number,
        indexSpeakerInOrg: number,
        attendeeId: number,
    ): void => {
        this.setProfileImage(updatedProfileImage, indexOrg, indexSpeakerInOrg);
        this.removeAssetFromUncategorizedFiles(draggingFile.objectKey);
        this.setIsLoadingProfileImage(false, indexOrg, indexSpeakerInOrg);
        this.trackAssetsMixPanel(TRACKING_EVENTS.ADD_PROFILE_IMAGE, attendeeId);
    };

    @mobx.action
    public uploadProfileImageFromUncategorized = async (draggingFile: ITitleAssetUrlDTO, organizationId: string, attendeeId: string,
    ): Promise<boolean> => {
        if (!this.fileDownloadAllowed) {
            this.showDragAndDropIsNotAllowedNotification();
            return false;
        }

        const { indexOrg, indexSpeakerInOrg } = this.getIndexesOrgAndSpeaker(Number(organizationId), Number(attendeeId));
        const isImageValid = await this.validateProfileImage(null, draggingFile, indexOrg, indexSpeakerInOrg);
        if (!isImageValid) return false;

        try {
            mobx.runInAction(() => {
                this.setIsLoadingProfileImage(true, indexOrg, indexSpeakerInOrg);
            });

            const response = await this.assetsDAO.updateProfileImage({
                    eventId: this.eventId,
                    accountId: this.accountId,
                    attendeeId: Number(attendeeId),
                },
                {
                    objectKey: draggingFile.objectKey,
                    title: draggingFile.title,
                    assetMetadataId: draggingFile.assetMetadataId
                }
            );

            const updatedProfileImage: ITitleAssetUrlDTO = {
                title: response.profileImageWithUrl.title,
                assetUrl: response.profileImageWithUrl.assetUrl,
                objectKey: response.profileImage.objectKey,
                assetMetadataId: response.profileImageWithUrl.assetMetadataId
            };

            mobx.runInAction(() => {
                this.completeProfileImageUploading(updatedProfileImage, draggingFile, indexOrg, indexSpeakerInOrg, Number(attendeeId));
            });

        } catch (e) {
            AssetsViewModel.handleApiException(e);
            this.setIsLoadingProfileImage(false, indexOrg, indexSpeakerInOrg);
            return false;
        }

    };

    @mobx.action
    public validateProfileImage = async (
        file: File,
        draggingFile: ITitleAssetUrlDTO,
        indexOrg: number,
        indexSpeakerInOrg: number,
    ): Promise<boolean> => {
        if (!await this.validateImageFormat(file, draggingFile, indexOrg, indexSpeakerInOrg)) return false;
        if (!this.validateImageSize(file, indexOrg, indexSpeakerInOrg)) return false;
        if (!await this.validateImageDimensions(file, draggingFile, indexOrg, indexSpeakerInOrg)) return false;
        return true;
    };

    private validateImageFormat = async (
        file: File,
        draggingFile: ITitleAssetUrlDTO,
        indexOrg: number,
        indexSpeakerInOrg: number,
    ): Promise<boolean> => {
        const isValid = file
            ? await this.validateFileTypeProfileImage(file, '')
            : await this.validateFileTypeProfileImage(null, draggingFile.title);
        mobx.runInAction(() => {
            if (!isValid) {
                this.setProfileImageValidationError(IMAGE_VALIDATION_FORMAT_ERROR_MESSAGE, indexOrg, indexSpeakerInOrg);
            } else {
                this.resetProfileImageValidationError(indexOrg, indexSpeakerInOrg);
            }
        });
        return isValid;
    };

    private validateImageSize = (
        file: File,
        indexOrg: number,
        indexSpeakerInOrg: number,
    ): boolean => {
        if (!file) return true;
        const isValid = isFileSizeValid(file);
        mobx.runInAction(() => {
            if (!isValid) {
                this.setProfileImageValidationError(IMAGE_VALIDATION_SIZE_ERROR_MESSAGE, indexOrg, indexSpeakerInOrg);
            } else {
                this.resetProfileImageValidationError(indexOrg, indexSpeakerInOrg);
            }
        });
        return isValid;
    };

    private validateImageDimensions = async (
        file: File,
        draggingFile: ITitleAssetUrlDTO,
        indexOrg: number,
        indexSpeakerInOrg: number,
    ): Promise<boolean> => {
        let isValid: boolean;
        if (file) {
            isValid = await checkFileMinWidthHeight(file, DIMENSIONS_PROFILE_IMAGE);
        } else {
            const { downloadUrl } = await this.assetsDAO.getAssetFileDownloadUrl({
                accountId: this.accountId,
                eventId: this.eventId,
                assetMetadataId: draggingFile.assetMetadataId
            });
            isValid = await checkFileMinWidthHeight(null, DIMENSIONS_PROFILE_IMAGE, downloadUrl);
        }
        mobx.runInAction(() => {
            if (!isValid) {
                this.setProfileImageValidationError(
                    'Profile Image ' + IMAGE_VALIDATION_MIN_WIDTH_HEIGHT_ERROR_MESSAGE(DIMENSIONS_PROFILE_IMAGE, this.intl),
                    indexOrg,
                    indexSpeakerInOrg
                );
            } else {
                this.resetProfileImageValidationError(indexOrg, indexSpeakerInOrg);
            }
        });
        return isValid;
    };

    @mobx.action
    public uploadProfileImage = async (file: File, organizationId: number, attendeeId: number): Promise<void> => {
        if (!file) return;
        const updatedFile = replaceCommaAndSemicolon(file);

        const { indexOrg, indexSpeakerInOrg } = this.getIndexesOrgAndSpeaker(organizationId, attendeeId);
        const isImageValid = await this.validateProfileImage(updatedFile, null, indexOrg, indexSpeakerInOrg);

        if (!isImageValid) return;

        try {
            mobx.runInAction(() => {
                this.setIsLoadingProfileImage(true, indexOrg, indexSpeakerInOrg);
            });
            const response = await this.assetsDAO.addProfileImageWithS3({
                accountId: this.accountId,
                eventId: this.eventId,
                attendeeId: attendeeId
            }, updatedFile);

            const updatedProfileImage: ITitleAssetUrlDTO = {
                title: response.profileImageWithUrl.title,
                assetUrl: response.profileImageWithUrl.assetUrl,
                objectKey: response.profileImage.objectKey,
                assetMetadataId: response.profileImageWithUrl.assetMetadataId
            };

            mobx.runInAction(() => {
                this.setProfileImage(updatedProfileImage, indexOrg, indexSpeakerInOrg);
                this.trackAssetsMixPanel(TRACKING_EVENTS.ADD_PROFILE_IMAGE, attendeeId);
            });

        } catch (e) {
            AssetsViewModel.handleApiException(e);
        } finally {
            this.setIsLoadingProfileImage(false, indexOrg, indexSpeakerInOrg);
        }
    };

    @mobx.computed
    public get hasFiles(): boolean {
        return (this.assetsList?.files?.length ?? 0) > 0 || this.selectedFiles.length > 0;
    }


    @mobx.action
    public setSortParams = (sortField: TGetReportsSortKeys, sortOrder: SortOrder): void => {
        this.sortField = sortField;
        this.sortOrder = sortOrder;
    };

    public isLoading = (): boolean => {
        return this.loading;
    };

    public isLoadingOrganizations = (): boolean => {
        return this.loadingOrganizations;
    };

    @mobx.action
    public onCollapseGroup = (group: string): void => {
        this.collapsedGroups = [...this.collapsedGroups, group];
    };

    @mobx.action
    public onExpandGroup = (group: string): void => {
        this.collapsedGroups = this.collapsedGroups.filter(
            el => el !== group
        );
    };

    @mobx.action
    public setSpeakerBiography = (biography: string, organizationId: number, attendeeId: number): void => {
        const { indexOrg, indexSpeakerInOrg } = this.getIndexesOrgAndSpeaker(Number(organizationId), Number(attendeeId));
        if (indexOrg > -1 && indexSpeakerInOrg > -1) {
            this.assetsSpeakersGroupedList[indexOrg].speakers[indexSpeakerInOrg].biography = biography;
            this.assetsSpeakersGroupedList[indexOrg].speakers[indexSpeakerInOrg].isBiographyAdded = true;
            setTimeout(() => {
                mobx.runInAction(() => this.assetsSpeakersGroupedList[indexOrg].speakers[indexSpeakerInOrg].isBiographyAdded = false);
            }, 3000);
        }
    };

    public getMixinAccountNameAndEventId = (): { [key: string]: string } => ({
        'Account Name': this.globalStorage.getEvent()?.accountName,
        'Event ID': this.eventId.toString(),
        'Event Name': this.globalStorage.getEvent()?.name.toString(),
    });

    public trackAssetsMixPanel = (trackingEvent: TRACKING_EVENTS, attendeeId: number = null): void => {
        const currentUrl = window.location.href;
        this.trackingService.get().track(trackingEvent, {
            ...this.getMixinAccountNameAndEventId(),
            ...(attendeeId !== null && { 'Attendee ID': attendeeId }),
            'Event URL': currentUrl,
        });
    };

    public getPagingVM = (): PagingViewModel => {
        return this.paginationVM;
    };

    public trackCreateAssetDownloadLinkMixPanel = (fileId: number): void => {
        this.trackingService.get().track(ASSETS_PAGE_TRACKING_EVENTS.CREATE_ASSET_DOWNLOAD_LINK, {
            'Event ID': this.eventId,
            'Event Name': this.globalStorage.getEvent()?.name.toString(),
            'Account ID': this.accountId,
            'Account Name': this.globalStorage.getEvent()?.accountName,
            'File ID' : fileId
        });
    };

    @mobx.action
    public createLink = async (
        assetMetadataId: number,
        afterCreateLinkCb: () => void
    ): Promise<void> => {
        if (this.isLinkCreating.has(assetMetadataId)) return;

        try {
            this.isLinkCreating.add(assetMetadataId);
            const response: ITitleAssetUrlDTO = await this.assetsDAO.createAssetLink({
                accountId: this.accountId,
                eventId: this.eventId,
                assetMetadataId: assetMetadataId
            });
            this.updateAssetFile(response);
            this.trackCreateAssetDownloadLinkMixPanel(assetMetadataId);
            if (!isFilePublic(response)) {
                throw new Error('An error occurred during link creation. Please try again later.');
            } else {
                afterCreateLinkCb();
            }
        } catch (e) {
            AssetsViewModel.handleApiException(e);
            mobx.runInAction(() => {
                this.isLinkCreating.delete(assetMetadataId);
            });
        }
    };

    @mobx.action
    public onDeleteAssetDownloadLink = (assetMetadataId: number): void => {
        this.alertsService.confirm({
            message: (
                <span className='alert-message'>
                    <FormattedMessage
                        id='oe.confirm.areYouSureTo'
                        description='Confirm message'
                        defaultMessage='Are you sure you want to'
                    />
                    <Space />
                    <span className='alert-message--danger'>
                        <FormattedMessage
                            id='oe.assets.deleteThisLink'
                            description='Confirm message'
                            defaultMessage='Delete this link'
                        />
                    </span>
                    ?<Space /><FormattedMessage
                        id='oe.artifacts.linkNoLongerDownload'
                        description='Alert message'
                        defaultMessage='Once deleted, the link will no longer download the file,
                            and this cannot be undone, but a new link can be generated.'
                    />
                </span>
            ),
            confirmButtonProps: {
                text: (
                    <FormattedMessage
                        id='oe.assets.confirmDelete'
                        description='label text'
                        defaultMessage='Yes, Delete'
                    />
                ),
                modifier: 'confirm-warn',
                handler: () => this.onDeleteSingleAssetDownloadLink(assetMetadataId)
            },
            cancelButtonProps: {
                modifier: 'cancel'
            }
        });
    };

    @mobx.action
    public onDeleteSingleAssetDownloadLink = async (assetMetadataId: number): Promise<void> => {
        try {
            await this.assetsDAO.deleteAssetLink({
                accountId: this.accountId,
                eventId: this.eventId,
                assetMetadataId: assetMetadataId,
            });
            await this.fetchAssets();
        } catch (error) {
            AssetsViewModel.handleApiException(error);
        }
    };

    @mobx.action
    private updateAssetFile = (file: ITitleAssetUrlDTO): void => {
        if (!this.assetsList.files.length) return;

        const fileIndexToUpdate = this.assetsList.files?.findIndex(a => a.assetMetadataId === file.assetMetadataId);
        this.assetsList.files[fileIndexToUpdate] = { ...file };
        this.isLinkCreating.delete(file.assetMetadataId);
    };

}

export default AssetsViewModel;
